import { z } from "zod";
import { emailValidation, createStringValidation } from "@/lib/validations/common";

const eventRoomSchema = z.object({
  name: z.string().min(1, "El nombre del salón es requerido"),
  capacity: z.number().int().min(1, "La capacidad debe ser mayor a 0"),
});

const rateSchema = z.object({
  roomTypeId: z.number().int().min(1, "El tipo de habitación es requerido"),
  bathroomTypeId: z.number().int().min(1, "El tipo de baño es requerido"),
  categoryId: z.number().int().min(1, "La categoría es requerida"),
  currencyId: z.number().int().min(1, "La moneda es requerida"),
  price: z.number().min(0, "El precio debe ser mayor o igual a 0"),
});

const servicesSchema = z.object({
  cards: z.array(z.string()).default([]),
  parking: z.array(z.string()).default([]),
  pool: z.array(z.string()).default([]),
  recreationalSports: z.array(z.string()).default([]),
  specialMenus: z.array(z.string()).default([]),
  
  spa: z.boolean().default(false),
  gym: z.boolean().default(false),
  wifi: z.boolean().default(false),
  breakfast: z.boolean().default(false),
  casino: z.boolean().default(false),
  petsAllowed: z.boolean().default(false),
  cafeteriaBar: z.boolean().default(false),
  restaurant: z.boolean().default(false),
  isAccessible: z.boolean().default(false),
  tv: z.boolean().default(false),
  airConditioning: z.boolean().default(false),
  heating: z.boolean().default(false),
  safeBox: z.boolean().default(false),
  laundryService: z.boolean().default(false),
  frontdesk24h: z.boolean().default(false),
  smokingArea: z.boolean().default(false),

  eventRooms: z.array(eventRoomSchema).default([]),

  serviceNotes: z.string().default(''),
});

const repatSchema = z.object({
  // Habitaciones
  roomsSingle: z.number().int().min(0).default(0),
  roomsDouble: z.number().int().min(0).default(0),
  roomsTriple: z.number().int().min(0).default(0),
  roomsQuadruple: z.number().int().min(0).default(0),
  roomsQuintuple: z.number().int().min(0).default(0),
  roomsSextuple: z.number().int().min(0).default(0),
  roomsExtraBeds: z.number().int().min(0).default(0),

  // Apartamentos
  apartmentsSingle: z.number().int().min(0).default(0),
  apartmentsDouble: z.number().int().min(0).default(0),
  apartmentsTriple: z.number().int().min(0).default(0),
  apartmentsQuadruple: z.number().int().min(0).default(0),
  apartmentsQuintuple: z.number().int().min(0).default(0),
  apartmentsSextuple: z.number().int().min(0).default(0),

  // Totales de Apartamentos
  totalApartments: z.number().int().min(0).default(0),
  totalApartmentPlaces: z.number().int().min(0).default(0),

  // Cabañas
  cabinsDouble: z.number().int().min(0).default(0),
  cabinsTriple: z.number().int().min(0).default(0),
  cabinsQuadruple: z.number().int().min(0).default(0),
  cabinsQuintuple: z.number().int().min(0).default(0),
  cabinsSextuple: z.number().int().min(0).default(0),
  cabinsExtraBeds: z.number().int().min(0).default(0),

  // Camping
  camping: z.boolean().default(false),
  campingCapacity: z.number().int().min(0).default(0),
});

const samplingBlockReasonSchema = z.object({
  id: z.number().describe('ID del estado de la reserva'),
  name: z.string().describe('Nombre del estado de la reserva'),
});

export const accommodationSchema = z.object({
  id: z.string().nullable().optional(),
  fileNumber: createStringValidation(1, 4),
  establishmentName: createStringValidation(1, 255),
  fileUpdatedDate: z.string().nullable().optional(),
  statusId: z.number().nullable().optional(),
  substatusId: z.number().nullable().optional(),
  statusDate: z.string().nullable().optional(),
  statusUntilDate: z.string().nullable().optional(),

  enabledDate: z.string().nullable().optional(),
  lastInspectionDate: z.string().nullable().optional(),
  isTouristComplex: z.boolean().default(false),

  classId: z.number().nullable().optional(),
  categoryId: z.number().nullable().optional(),

  street: z.string().optional(),
  streetNumber: z.string().optional(),
  floor: z.string().optional(),
  neighborhood: z.string().optional(),
  localityId: z.number().nullable().optional(),

  geolocation: z.string().optional(),
  email: z.string().optional(),
  website: z.string().optional(),
  phone: z.string().optional(),
  whatsapp: z.string().optional(),
  instagram: z.string().optional(),
  facebook: z.string().optional(),
  youtube: z.string().optional(),

  ownerName: z.string().optional(),
  cuit: z.string().optional(),
  ownerEmail: z.string().optional(),
  managerName: z.string().optional(),

  generalNotes: z.string().optional(),

  observatoryNotes: z.string().nullable().optional(),
  sampleTypeid: z.number().nullable().optional(),
  samplingBlockReasonId: z.number().nullable().optional(),
  samplingBlockReason: samplingBlockReasonSchema.nullable().optional(),
  samplingBlockDate: z.string().nullable().optional(),

  rateStartDate: z.string().nullable().optional(),
  rateEndDate: z.string().nullable().optional(),

  rates: z.array(rateSchema).refine(
    (rates) => {
      const combinations = new Set();
      return rates.every((rate) => {
        const key = `${rate.roomTypeId}-${rate.bathroomTypeId}-${rate.categoryId}-${rate.currencyId}`;
        if (combinations.has(key)) return false;
        combinations.add(key);
        return true;
      });
    },
    {
      message: "No puede haber combinaciones duplicadas de tipo de habitación, baño, categoría y moneda",
    }
  ).default([]),

  services: servicesSchema.default({}),
  repat: repatSchema.default({}),
});